import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Activity,
  AlertCircle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  BarChart3,
  History,
  Loader2,
  RefreshCw,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import OddsHistoryOutcomeCard from "../components/operator/OddsHistoryOutcomeCard";
import OperatorConsoleShell from "../components/operator/OperatorConsoleShell";
import { selectAppUser } from "../redux/appAuthSlice";
import {
  clearOddsHistory,
  fetchHouseFixtures,
  fetchOddsHistory,
} from "../redux/operatorConsoleSlice";
import {
  fixtureKickoffShort,
  fixtureMatchLabel,
  resolveFixtureId,
} from "../utils/fixturesDeskHelpers";
import {
  buildOddsHistorySummary,
  formatOddsValue,
  getOddsMove,
  groupOddsHistory,
} from "../utils/oddsHistoryHelpers";
import "../bettors-desk.css";

const BIAS_META = {
  rising: { label: "Mostly rising", Icon: TrendingUp, tone: "up" },
  dropping: { label: "Mostly dropping", Icon: TrendingDown, tone: "down" },
  balanced: { label: "Balanced moves", Icon: Activity, tone: "neutral" },
};

const OddsHistoryDesk = () => {
  const { houseId, fixtureId } = useParams();
  const dispatch = useDispatch();
  const userId = useSelector(selectAppUser)?._id;

  const {
    houses,
    houseFixtures,
    houseFixturesHouseId,
    oddsHistoryEntries,
    oddsHistoryKey,
    oddsHistoryStatus,
    oddsHistoryError,
  } = useSelector((s) => s.operatorConsole);

  const [refreshing, setRefreshing] = useState(false);
  const [selectedOutcomeKey, setSelectedOutcomeKey] = useState(null);

  const cacheKey = houseId && fixtureId ? `${houseId}:${fixtureId}` : null;
  const fixturesPath = `/app/bethouse/manage/${houseId}/fixtures`;

  const bet = useMemo(
    () => houses.find((h) => String(h._id) === String(houseId)),
    [houses, houseId]
  );

  const fixture = useMemo(
    () =>
      houseFixtures.find((f) => String(resolveFixtureId(f)) === String(fixtureId)) || null,
    [houseFixtures, fixtureId]
  );

  const houseName = bet?.name || "House";
  const matchLabel = fixture ? fixtureMatchLabel(fixture) : "Fixture";

  const reload = useCallback(() => {
    if (!houseId || !fixtureId) return Promise.resolve();
    return dispatch(fetchOddsHistory({ houseId, fixtureId }));
  }, [dispatch, houseId, fixtureId]);

  useEffect(() => {
    if (!houseId) return undefined;
    if (houseFixturesHouseId !== houseId) {
      dispatch(fetchHouseFixtures({ betId: houseId }));
    }
    return undefined;
  }, [dispatch, houseId, houseFixturesHouseId]);

  useEffect(() => {
    reload();
    return () => dispatch(clearOddsHistory());
  }, [reload, dispatch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await reload();
    } finally {
      setRefreshing(false);
    }
  };

  const rows =
    oddsHistoryKey === cacheKey && Array.isArray(oddsHistoryEntries)
      ? oddsHistoryEntries
      : [];

  const groupedOutcomes = useMemo(() => groupOddsHistory(rows), [rows]);
  const summary = useMemo(
    () => buildOddsHistorySummary(rows, groupedOutcomes),
    [rows, groupedOutcomes]
  );
  const bias = BIAS_META[summary.bias] || BIAS_META.balanced;
  const BiasIcon = bias.Icon;
  const loading = oddsHistoryStatus === "loading" && !rows.length;

  useEffect(() => {
    if (!groupedOutcomes.length) {
      setSelectedOutcomeKey(null);
      return;
    }
    setSelectedOutcomeKey((prev) =>
      prev && groupedOutcomes.some((g) => g.outcomeKey === prev)
        ? prev
        : groupedOutcomes[0].outcomeKey
    );
  }, [groupedOutcomes]);

  const selectedGroup = useMemo(
    () => groupedOutcomes.find((g) => g.outcomeKey === selectedOutcomeKey) || null,
    [groupedOutcomes, selectedOutcomeKey]
  );

  const headerLine = useMemo(() => {
    const parts = [matchLabel];
    if (fixture?.date) {
      parts.push(fixtureKickoffShort(fixture));
    }
    if (rows.length) {
      parts.push(`${summary.totalChanges} changes`);
      parts.push(`${summary.markets} markets`);
      parts.push(bias.label.toLowerCase());
    }
    return parts.join(" · ");
  }, [matchLabel, fixture, rows.length, summary, bias.label]);

  if (!houseId || !fixtureId) {
    return (
      <OperatorConsoleShell activeSection="fixtures" mainClassName="oc-desk-main">
        <div className="bd-page fd-page oh-page oh-history-desk">
          <div className="bd-loading">Missing house or fixture.</div>
        </div>
      </OperatorConsoleShell>
    );
  }

  return (
    <OperatorConsoleShell activeSection="fixtures" mainClassName="oc-desk-main">
      <div className="bd-page fd-page oh-page oh-history-desk">
        <header className="bd-header oh-history-header">
          <div className="bd-header-copy">
            <p className="bd-eyebrow">
              <History size={9} />
              Odds history
            </p>
            <span className="bd-header-title">
              {houseName}
              {rows.length || fixture ? ` · ${headerLine}` : ""}
            </span>
          </div>
          <div className="oh-history-header-actions">
            <Link to={fixturesPath} className="bd-icon-btn" aria-label="Back to board">
              <ArrowLeft size={13} />
            </Link>
            <button
              type="button"
              className="bd-icon-btn"
              onClick={handleRefresh}
              disabled={refreshing || oddsHistoryStatus === "loading"}
              aria-label="Refresh odds history"
            >
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>
        </header>

        <div className="bd-desk-body">
          {loading ? (
            <div className="bd-loading bd-loading-compact oh-loading">
              <Loader2 size={18} className="animate-spin" />
              <span>Loading odds history…</span>
            </div>
          ) : null}

          {oddsHistoryError && oddsHistoryStatus === "failed" ? (
            <div className="fd-alert fd-alert-error oh-alert" role="alert">
              <AlertCircle size={15} aria-hidden />
              <p>{String(oddsHistoryError)}</p>
              <button type="button" onClick={reload}>
                Retry
              </button>
            </div>
          ) : null}

          {!loading && !rows.length && oddsHistoryStatus !== "failed" ? (
            <div className="bd-empty-state oh-empty">
              <span className="oh-empty-icon" aria-hidden>
                <BarChart3 size={20} />
              </span>
              <strong>No odds changes yet</strong>
              <p>
                Every adjustment for {matchLabel} will appear here with the operator,
                timestamp, and direction.
              </p>
              <Link to={fixturesPath} className="oc-odds-empty-link">
                Return to board lanes
              </Link>
            </div>
          ) : null}

          {!loading && rows.length ? (
            <div className="bd-bettors-command oh-history-command">
              <aside className="bd-roster-rail oh-history-rail">
                <section className="oh-history-summary" aria-label="History summary">
                  <div className="oh-history-summary-head">
                    <span className={`oh-bias-pill oh-bias-pill-${bias.tone}`}>
                      <BiasIcon size={10} aria-hidden />
                      {bias.label}
                    </span>
                    <time className="oh-history-summary-time">{summary.latestChangedAt}</time>
                  </div>
                  <div className="oh-history-summary-grid">
                    <div className="oh-history-summary-stat">
                      <em>Changes</em>
                      <strong className="tabular-nums">{summary.totalChanges}</strong>
                    </div>
                    <div className="oh-history-summary-stat">
                      <em>Markets</em>
                      <strong className="tabular-nums">{summary.markets}</strong>
                    </div>
                    <div className="oh-history-summary-stat">
                      <em>Rose</em>
                      <strong className="tabular-nums oh-stat-up">{summary.upMoves}</strong>
                    </div>
                    <div className="oh-history-summary-stat">
                      <em>Dropped</em>
                      <strong className="tabular-nums oh-stat-down">{summary.downMoves}</strong>
                    </div>
                  </div>
                  <div className="oh-history-move-row">
                    <span>
                      <ArrowUp size={11} aria-hidden />
                      {summary.upMoves} up
                    </span>
                    <span>
                      <ArrowDown size={11} aria-hidden />
                      {summary.downMoves} down
                    </span>
                  </div>
                </section>

                <section className="bd-roster-rail-scroll oh-history-rail-scroll">
                  <header className="bd-rail-head">
                    <h2>Markets</h2>
                    <span className="tabular-nums">{groupedOutcomes.length}</span>
                  </header>
                  <ul className="oh-outcome-roster">
                    {groupedOutcomes.map((group) => {
                      const move = getOddsMove(group.latest);
                      const active = group.outcomeKey === selectedOutcomeKey;
                      return (
                        <li key={group.outcomeKey}>
                          <button
                            type="button"
                            className={[
                              "oh-outcome-roster-row",
                              `oh-outcome-roster-row-${move.tone}`,
                              active ? "oh-outcome-roster-row-active" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            onClick={() => setSelectedOutcomeKey(group.outcomeKey)}
                            aria-current={active ? "true" : undefined}
                          >
                            <span className="oh-outcome-roster-copy">
                              <strong>{group.outcomeLabel}</strong>
                              <em>
                                {group.count} change{group.count === 1 ? "" : "s"}
                              </em>
                            </span>
                            <span className="oh-outcome-roster-odds tabular-nums">
                              {formatOddsValue(group.latest?.oldOdds)}
                              <span aria-hidden>→</span>
                              {formatOddsValue(group.latest?.newOdds)}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              </aside>

              <div className="oh-history-workspace">
                {selectedGroup ? (
                  <OddsHistoryOutcomeCard group={selectedGroup} layout="workspace" />
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </OperatorConsoleShell>
  );
};

export default OddsHistoryDesk;
